import { useCallback, useState } from "react";
import type { ExpressCartData } from "@/api/express-cart-loaders";
import { BrandThemeProvider, useBrandTheme } from "@/lib/brand-theme";
import { Card } from "@/components/Card";
import { ErrorMessage } from "@/components/ErrorMessage";
import { useCartSession, type CartSession } from "@/hooks/useCartSession";
import { brandLinkChatUrl, linkSource, sessionChatUrl, whatsAppReturnUrl } from "@/lib/express-cart/link-source";
import { expressCartApi } from "@/api/client";
import { saveExpressSession } from "@/lib/express-cart/device";
import { ExpressCart } from "./ExpressCart";
import { ExpressCartLayout } from "./ExpressCartLayout";

interface Props {
  data: ExpressCartData;
  /** "view-cart": opened from the agent's View Cart link (only changes the empty-cart message). */
  source: "link" | "view-cart";
  /** For a brand link without a session: creates one on the first saved edit. */
  createSession?: () => Promise<CartSession>;
  /** Why the checkout sent the shopper back here, e.g. items out of stock. */
  notice?: string | null;
}

function Cart({ data, createSession, notice }: Omit<Props, "source">) {
  const { syncError, ...controller } = useCartSession(data.link, data.items, {
    session: data.session,
    createSession,
    retailerId: data.brand.id,
    integrationType: data.integrationType,
    // Same rule as the agent's checkout tool: public and hybrid Shopify brands pay on Shopify.
    checkout: ["shopify_public", "shopify_hybrid"].includes(data.integrationType ?? "")
      ? "shopify"
      : "nile",
  });
  return (
    <>
      {notice && (
        <div className="mx-auto max-w-5xl px-4 pt-6">
          <ErrorMessage title="Your cart has changed" message={notice} />
        </div>
      )}
      {syncError && (
        <div className="mx-auto max-w-5xl px-4 pt-6">
          <ErrorMessage message={syncError} />
        </div>
      )}
      <ExpressCart link={data.link} controller={controller} integrationType={data.integrationType} />
    </>
  );
}

/** The express cart once its data has loaded; shared by /cart/:slug and /cart. */
export function ExpressCartView(props: Props) {
  return (
    <BrandThemeProvider theme={props.data.brand}>
      <ExpressCartContent {...props} />
    </BrandThemeProvider>
  );
}

function ExpressCartContent({ data, source, createSession, notice }: Props) {
  const { theme } = useBrandTheme();
  const brand = theme ?? data.brand;
  // Opened from a WhatsApp chat (the agent's links carry ?src=whatsapp).
  // Back to the chat without pre-filled text: they're already in a conversation.
  const [chatReturnUrl] = useState(() =>
    linkSource() === "whatsapp"
      ? whatsAppReturnUrl(data.brandChatUrl ?? (data.link.code ? brandLinkChatUrl(data.link.code) : null))
      : null,
  );
  // The WhatsApp button; switches to the cart question once a session exists.
  const [whatsappUrl, setWhatsappUrl] = useState(data.link.whatsapp_url);
  const createSessionAndWhatsApp = useCallback(async (): Promise<CartSession> => {
    const created = createSession
      ? await createSession()
      : await expressCartApi.createSession({
          retailerId: data.brand.id,
          linkSlug: data.link.code,
        }).then((result) => {
          if (data.link.code) saveExpressSession(data.link.code, result);
          return { session_id: result.session_id, session_token: result.session_token };
        });
    void expressCartApi.getWhatsAppContact(created.session_token).then((contact) => {
      if (contact?.whatsappUrl) setWhatsappUrl(sessionChatUrl(contact.whatsappUrl, created.session_id));
    });
    return created;
  }, [createSession, data.brand.id, data.link.code]);

  return (
    <ExpressCartLayout
      brand={brand}
      whatsappUrl={whatsappUrl}
      chatReturnUrl={chatReturnUrl}
    >
      {data.link.products.length === 0 ? (
        <div className="min-h-screen flex items-center justify-center p-4 bg-(--color-bg-primary)">
          <Card className="max-w-sm w-full text-center">
            <div className="empty-state py-4">
              <svg
                className="empty-state-icon"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                />
              </svg>
              <h2 className="text-[18px] font-semibold text-(--color-text-primary)">
                {source === "view-cart" ? "Your cart is empty" : "No products on this link"}
              </h2>
              <p className="text-[14px] text-(--color-text-muted) mt-2">
                {source === "view-cart"
                  ? "No items in your cart at the moment."
                  : "This link doesn't have any products available right now."}
              </p>
            </div>
          </Card>
        </div>
      ) : (
        <Cart data={data} createSession={createSessionAndWhatsApp} notice={notice} />
      )}
    </ExpressCartLayout>
  );
}
